// src/utils/mobileMedia.js
import { Camera } from '@capacitor/camera';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

/**
 * Detecta si la app está corriendo en una plataforma nativa
 */
export const isNativePlatform = () => {
    return Capacitor.isNativePlatform();
};

/**
 * Toma una foto usando la cámara del dispositivo
 * @param {Object} options - Opciones de captura
 * @param {string} options.quality - Calidad de la imagen (0-100)
 * @param {boolean} options.allowEditing - Permitir edición antes de guardar
 * @param {string} options.resultType - 'base64', 'dataUrl', o 'uri'
 * @returns {Promise<{file: File, dataUrl: string, path: string}>}
 */
export const takePhoto = async (options = {}) => {
    const isNative = isNativePlatform();

    if (isNative) {
        try {
            const image = await Camera.getPhoto({
                quality: options.quality || 90,
                allowEditing: options.allowEditing || false,
                resultType: 'base64',
                source: 'CAMERA',
                ...options,
            });

            // Convertir base64 a File
            const base64Response = await fetch(`data:image/${image.format};base64,${image.base64String}`);
            const blob = await base64Response.blob();
            const file = new File([blob], `photo_${Date.now()}.${image.format}`, {
                type: `image/${image.format}`,
            });

            return {
                file,
                dataUrl: `data:image/${image.format};base64,${image.base64String}`,
                path: image.path,
            };
        } catch (error) {
            console.error('Error al tomar foto con Camera plugin:', error);
            throw error;
        }
    } else {
        // Fallback a input file en navegador
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.capture = 'environment';
            input.onchange = async (e) => {
                const file = e.target.files?.[0];
                if (!file) {
                    reject(new Error('No se seleccionó ningún archivo'));
                    return;
                }

                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve({
                        file,
                        dataUrl: event.target.result,
                        path: null,
                    });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            };
            input.oncancel = () => reject(new Error('Captura cancelada'));
            input.click();
        });
    }
};

/**
 * Graba un video usando la cámara del dispositivo
 * @param {Object} options - Opciones de grabación
 * @param {string} options.quality - Calidad del video
 * @returns {Promise<{file: File, dataUrl: string, path: string}>}
 */
export const recordVideo = async (options = {}) => {
    const isNative = isNativePlatform();

    if (isNative) {
        try {
            const video = await Camera.getPhoto({
                quality: options.quality || 90,
                allowEditing: options.allowEditing || false,
                resultType: 'base64',
                source: 'CAMERA',
                mediaType: 'video',
                ...options,
            });

            // Convertir base64 a File
            const base64Response = await fetch(`data:video/${video.format || 'mp4'};base64,${video.base64String}`);
            const blob = await base64Response.blob();
            const file = new File([blob], `video_${Date.now()}.${video.format || 'mp4'}`, {
                type: `video/${video.format || 'mp4'}`,
            });

            return {
                file,
                dataUrl: `data:video/${video.format || 'mp4'};base64,${video.base64String}`,
                path: video.path,
            };
        } catch (error) {
            console.error('Error al grabar video con Camera plugin:', error);
            throw error;
        }
    } else {
        // Fallback a input file en navegador
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'video/*';
            input.capture = 'environment';
            input.onchange = async (e) => {
                const file = e.target.files?.[0];
                if (!file) {
                    reject(new Error('No se seleccionó ningún archivo'));
                    return;
                }

                const reader = new FileReader();
                reader.onload = (event) => {
                    resolve({
                        file,
                        dataUrl: event.target.result,
                        path: null,
                    });
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            };
            input.oncancel = () => reject(new Error('Grabación cancelada'));
            input.click();
        });
    }
};

/**
 * Selecciona un archivo del dispositivo
 * @param {Object} options - Opciones de selección
 * @param {string} options.accept - Tipos MIME aceptados (ej: 'image/*', 'application/pdf')
 * @param {boolean} options.multiple - Permitir múltiples archivos
 * @returns {Promise<File|File[]>}
 */
export const pickFile = async (options = {}) => {
    const isNative = isNativePlatform();

    if (isNative) {
        // En app nativa, usar el selector de archivos del sistema
        // Por ahora, usamos input file como fallback ya que Filesystem no tiene un picker directo
        // Podríamos usar @capacitor-community/file-picker si lo instalamos
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = options.accept || '*/*';
            input.multiple = options.multiple || false;
            input.onchange = (e) => {
                const files = Array.from(e.target.files || []);
                if (files.length === 0) {
                    reject(new Error('No se seleccionó ningún archivo'));
                    return;
                }
                resolve(options.multiple ? files : files[0]);
            };
            input.oncancel = () => reject(new Error('Selección cancelada'));
            input.click();
        });
    } else {
        // Navegador: usar input file
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = options.accept || '*/*';
            input.multiple = options.multiple || false;
            input.onchange = (e) => {
                const files = Array.from(e.target.files || []);
                if (files.length === 0) {
                    reject(new Error('No se seleccionó ningún archivo'));
                    return;
                }
                resolve(options.multiple ? files : files[0]);
            };
            input.oncancel = () => reject(new Error('Selección cancelada'));
            input.click();
        });
    }
};

/**
 * Graba audio usando el micrófono del dispositivo
 * @param {Object} options - Opciones de grabación
 * @param {number} options.maxDuration - Duración máxima en segundos (0 = sin límite)
 * @returns {Promise<{file: File, dataUrl: string, blob: Blob, stop: Function, pause: Function, resume: Function}>}
 */
export const recordAudio = async (options = {}) => {
    return new Promise(async (resolve, reject) => {
        try {
            // Solicitar permiso de micrófono
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks = [];
            let recordingPromise = null;

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                const file = new File([audioBlob], `audio_${Date.now()}.webm`, {
                    type: 'audio/webm',
                });

                const reader = new FileReader();
                reader.onload = (event) => {
                    // Detener el stream
                    stream.getTracks().forEach((track) => track.stop());
                    if (recordingPromise) {
                        recordingPromise.resolve({
                            file,
                            dataUrl: event.target.result,
                            blob: audioBlob,
                        });
                    }
                };
                reader.onerror = (error) => {
                    stream.getTracks().forEach((track) => track.stop());
                    if (recordingPromise) {
                        recordingPromise.reject(error);
                    }
                };
                reader.readAsDataURL(file);
            };

            mediaRecorder.onerror = (error) => {
                stream.getTracks().forEach((track) => track.stop());
                if (recordingPromise) {
                    recordingPromise.reject(error);
                }
            };

            // Iniciar grabación
            mediaRecorder.start();

            // Si hay duración máxima, detener automáticamente
            if (options.maxDuration && options.maxDuration > 0) {
                setTimeout(() => {
                    if (mediaRecorder.state !== 'inactive') {
                        mediaRecorder.stop();
                    }
                }, options.maxDuration * 1000);
            }

            // Crear Promise para cuando se detenga la grabación
            recordingPromise = {
                resolve: null,
                reject: null,
            };

            const recordingResultPromise = new Promise((res, rej) => {
                recordingPromise.resolve = res;
                recordingPromise.reject = rej;
            });

            // Retornar objeto con métodos de control y la promise del resultado
            resolve({
                stop: () => {
                    if (mediaRecorder.state !== 'inactive') {
                        mediaRecorder.stop();
                    }
                    return recordingResultPromise;
                },
                pause: () => {
                    if (mediaRecorder.state === 'recording') {
                        mediaRecorder.pause();
                    }
                },
                resume: () => {
                    if (mediaRecorder.state === 'paused') {
                        mediaRecorder.resume();
                    }
                },
                result: recordingResultPromise,
            });
        } catch (error) {
            console.error('Error al iniciar grabación de audio:', error);
            reject(error);
        }
    });
};

/**
 * Selecciona un archivo de audio existente
 * @returns {Promise<File>}
 */
export const pickAudioFile = async () => {
    return pickFile({ accept: 'audio/*' });
};

/**
 * Selecciona una imagen existente de la galería
 * @returns {Promise<File>}
 */
export const pickImage = async () => {
    // Para cumplir con la política de Google Play de no solicitar permisos amplios de fotos/videos,
    // usamos el selector del sistema (input file) en todas las plataformas, incluyendo nativa.
    return pickFile({ accept: 'image/*' });
};

/**
 * Selecciona un video existente de la galería
 * @returns {Promise<File>}
 */
export const pickVideo = async () => {
    return pickFile({ accept: 'video/*' });
};

/**
 * Guarda una imagen (Base64/DataURL) directamente en el almacenamiento de Documentos del dispositivo.
 * @param {string} dataUrl - La imagen en formato DataURL
 * @param {string} fileName - El nombre del archivo (ej: qr_invitado.png)
 * @returns {Promise<string>} - La URI del archivo guardado
 */
export const saveImageToDevice = async (dataUrl, fileName) => {
    if (!isNativePlatform()) {
        throw new Error('Solo disponible en plataforma nativa (Capacitor)');
    }

    try {
        // Extraer la parte Base64 del DataURL
        const base64Data = dataUrl.split(',')[1];
        if (!base64Data) {
            throw new Error('Formato de imagen inválido');
        }

        // Guardar en el directorio de Documentos (Standard en Android e iOS)
        const result = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Documents,
            recursive: true
        });

        return result.uri;
    } catch (error) {
        console.error('Error en saveImageToDevice:', error);
        throw error;
    }
};

